import React, { useContext, useEffect, useState } from "react";
import { useAppDispatch } from "store/hooks";
import * as FP from "utils/fp-js";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { FormSelectField } from "UI/FormSelectField";
import {
  emailValidation,
  phoneNumberValidation,
  nameValidation,
  required,
  notRequiredPhoneNumberValidation,
} from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { AddNewRegionDialog } from "./AddNewRegionDialog";
import { RequiredMark } from "UI/RequiredMark";
import PracticeSelector from "./PracticeSelector";
import { useCloseAndReset } from "hooks/useCloseAndReset";

/** @type {import("types/commonTypes").NewSpecialistData}*/
const defaultFormData = {
  role: "",
  organizationId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
  marketingRegionId: "",
};

const defaultPractice = { id: "", name: "" };

export const AddNewSpecialistDialog = ({
  title,
  role,
  apiPath,
  open,
  handleClose,
  refreshList,
  regionsOptions = {},
  refreshRegionsOptions = () => null,
  organizationId = "",
  organizationName = "",
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, userData } = useContext(GlobalContext);
  const [isAddNewRegionDialogOpen, setIsAddNewRegionDialogOpen] =
    useState(false);

  /** @type {import("types/commonTypes").Organization[]} */
  const initialState = [];
  const [recipients, setRecipients] = useState(initialState);
  const [practice, setPractice] = useState(defaultPractice);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    register,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    setPractice(defaultPractice);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    const specialist = getValues();

    const body = {
      specialist: {
        ...specialist,
        role,
        organizationId: ["Originator", "Subsender"].includes(role)
          ? specialist.organizationId
          : // @ts-ignore
            userData?.organizationId,
      },
    };

    body.specialist.firstName = body.specialist.firstName.trim();
    body.specialist.lastName = body.specialist.lastName.trim();
    body.specialist.jobTitle = body.specialist.jobTitle.trim();

    const result = await API.put("restApi", apiPath, {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {role === "Originator" ? "Sender" : role} added successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    forceCloseAndReset();
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  useEffect(() => {
    const getData = async () => {
      try {
        const result = await API.get("restApi", "/organizations", {});
        setRecipients(result?.Items ?? []);
      } catch (error) {}
    };

    getData();
  }, []);

  recipients.sort((a, b) => (a.name > b.name ? 1 : -1));

  useEffect(() => {
    if (practice?.id) {
      setValue("organizationId", practice.id, { shouldDirty: true });
      return;
    }
    setValue("organizationId", "", { shouldDirty: false });
  }, [practice, setValue]);

  useEffect(() => {
    if (organizationId && open) {
      setPractice({ id: organizationId, name: organizationName });
      setValue("organizationId", organizationId, { shouldDirty: false });
    }
  }, [organizationId, setValue, open, organizationName]);

  const isOriginator = role === "Originator";

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={title}
      content={
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack mt={2} gap={2}>
              <Stack direction={"row"} justifyContent={"space-between"} gap={2}>
                {FP.match(role)
                  .on(
                    ["Originator", "Subsender"],
                    <Stack alignItems={"flex-start"} flex={1}>
                      <InputLabel htmlFor="organizationId">
                        Practice <RequiredMark />
                      </InputLabel>
                      <input
                        type="hidden"
                        {...register("organizationId", { required })}
                      />
                      <PracticeSelector
                        setValue={setPractice}
                        value={practice}
                        error={errors?.organizationId?.message}
                      />
                    </Stack>
                  )
                  .otherwise(null)}
              </Stack>
              <Stack direction={"row"} gap={2}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="firstName">
                    First name <RequiredMark />
                  </InputLabel>
                  <FormTextField
                    id="firstName"
                    name="firstName"
                    control={control}
                    rules={nameValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="lastName">
                    Last name <RequiredMark />
                  </InputLabel>
                  <FormTextField
                    id="lastName"
                    name="lastName"
                    control={control}
                    rules={nameValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                  />
                </Stack>
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="phone">
                  {isOriginator ? (
                    <>Phone</>
                  ) : (
                    <>
                      Direct Phone <RequiredMark />
                    </>
                  )}
                </InputLabel>
                <FormMaskedTextField
                  id="phone"
                  name="phone"
                  control={control}
                  rules={
                    isOriginator
                      ? notRequiredPhoneNumberValidation
                      : phoneNumberValidation
                  }
                  mask="999-999-9999"
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="email">
                  Email for Referral Stream notifications <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="email"
                  name="email"
                  control={control}
                  rules={emailValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              {FP.match(role)
                .on(
                  ["Originator", "Subsender"],
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="jobTitle">Job title</InputLabel>
                    <FormTextField
                      id="jobTitle"
                      name="jobTitle"
                      control={control}
                      InputProps={{
                        autoComplete: "off",
                      }}
                    />
                  </Stack>
                )
                /* TODO - add marketing region list functionality */
                .on(
                  "Marketer",
                  <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                    <Stack alignItems={"flex-start"} flex="1">
                      <InputLabel htmlFor="marketingRegionId">
                        Marketing Region
                      </InputLabel>
                      <FormSelectField
                        id="marketingRegionId"
                        name="marketingRegionId"
                        control={control}
                        options={regionsOptions}
                      />
                    </Stack>
                    <Button onClick={() => setIsAddNewRegionDialogOpen(true)}>
                      Add new region
                    </Button>
                  </Stack>
                )
                .otherwise(null)}
            </Stack>
          </form>
          <AddNewRegionDialog
            open={isAddNewRegionDialogOpen}
            handleClose={() => setIsAddNewRegionDialogOpen(false)}
            refreshList={refreshRegionsOptions}
          />
        </>
      }
      actions={
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mb={2}
        >
          <Box ml={2} sx={{ color: "gray", fontSize: 14 }}>
            <RequiredMark /> These fields are required
          </Box>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Add New {role === "Originator" ? "Sender" : role}
          </Button>
        </Stack>
      }
    />
  );
};
